import { useEffect, useMemo, useState } from 'react';

import registerUrl from '@/assets/dietary-register.json?url';

import type { PersonPreferences } from './person.dto';

/**
 * Allergies and dietary exclusions: what the chef and crew must know before a trip.
 *
 * Each group is a short list of common picks, the VesselHaven dietary register behind a
 * search for anything else, free text for what the register lacks, and a note for the
 * chef. Picks are stored as register codes, so renaming a label is safe.
 *
 * The people service has no field for any of this beyond `allergies` (a string of up to
 * 2000 characters) and `dietary_restrictions` (a list of strings). So the answers go into
 * `allergies` as versioned JSON, and the dietary names are written to
 * `dietary_restrictions` as well, where the chef's guest summary reads them as words.
 * Anything stored before this, typed as plain text, is read back by matching each entry
 * against the register, and whatever does not match becomes free text.
 *
 * `src/assets/dietary-register.json` is the register's allergy, intolerance, medical,
 * religious, lifestyle and dislike entries, taken from `VesselHaven Dietary Register.html`
 * (schema 1.0, Oct 26, 2026): `[code, name, severity, aliases?]` per row.
 */

export type DietGroupId = 'allergy' | 'diet';

export type DietSeverity = 'life_threatening' | 'medical' | 'strict' | 'preference';

/** The food a pick names, for its glyph. */
export type DietGlyph =
  | 'nut'
  | 'milk'
  | 'egg'
  | 'fish'
  | 'shrimp'
  | 'wheat'
  | 'leaf'
  | 'meat'
  | 'pork'
  | 'wine';

export type DietQuickPick = {
  code: string;
  label: string;
  severity: DietSeverity;
  glyph?: DietGlyph;
};

export type DietGroup = {
  id: DietGroupId;
  title: string;
  /** The title in Title Case, for buttons and modal titles. */
  label: string;
  /** The question its onboarding step asks. */
  question: string;
  lead: string;
  searchPlaceholder: string;
  notePlaceholder: string;
  /** What the profile says when nothing is picked. */
  none: string;
  quick: readonly DietQuickPick[];
};

export const DIET_GROUPS: readonly DietGroup[] = [
  {
    id: 'allergy',
    title: 'Allergies',
    label: 'Allergies',
    question: 'Any allergies we should know about?',
    lead: 'We share this with the chef and crew before every trip.',
    searchPlaceholder: 'Kiwi, latex',
    notePlaceholder: 'Carries an EpiPen, reacts to cooking vapors',
    none: 'No allergies',
    quick: [
      { code: 'ALG-PEANUT', label: 'Peanuts', severity: 'life_threatening', glyph: 'nut' },
      { code: 'ALG-TREENUT', label: 'Tree nuts', severity: 'life_threatening', glyph: 'nut' },
      { code: 'ALG-MILK', label: 'Milk', severity: 'life_threatening', glyph: 'milk' },
      { code: 'ALG-EGG', label: 'Eggs', severity: 'life_threatening', glyph: 'egg' },
      { code: 'ALG-FISH', label: 'Fish', severity: 'life_threatening', glyph: 'fish' },
      {
        code: 'ALG-SHELLFISH-CRUST',
        label: 'Crustaceans',
        severity: 'life_threatening',
        glyph: 'shrimp',
      },
      { code: 'ALG-SHELLFISH-MOLL', label: 'Mollusks', severity: 'life_threatening' },
      { code: 'ALG-WHEAT', label: 'Wheat', severity: 'life_threatening', glyph: 'wheat' },
      { code: 'ALG-SOY', label: 'Soy', severity: 'life_threatening' },
      { code: 'ALG-SESAME', label: 'Sesame', severity: 'life_threatening' },
      { code: 'ALG-MUSTARD', label: 'Mustard', severity: 'life_threatening' },
      { code: 'ALG-CELERY', label: 'Celery', severity: 'life_threatening' },
    ],
  },
  {
    id: 'diet',
    title: 'Dietary exclusions',
    label: 'Dietary Exclusions',
    question: 'Your dietary needs',
    lead: 'Pick any diets you follow and anything to leave off your plate.',
    searchPlaceholder: 'No mushrooms, Jain, low FODMAP',
    notePlaceholder: 'How strict, any exceptions, anything we missed',
    none: 'No dietary exclusions',
    quick: [
      { code: 'LIFE-VEGETARIAN-LACTO-OVO', label: 'Vegetarian', severity: 'strict', glyph: 'leaf' },
      { code: 'LIFE-VEGAN', label: 'Vegan', severity: 'strict', glyph: 'leaf' },
      { code: 'LIFE-PESCATARIAN', label: 'Pescatarian', severity: 'preference', glyph: 'fish' },
      { code: 'LIFE-NORED', label: 'No red meat', severity: 'preference', glyph: 'meat' },
      { code: 'LIFE-NOPORK', label: 'No pork', severity: 'preference', glyph: 'pork' },
      { code: 'REL-HALAL', label: 'Halal', severity: 'strict' },
      { code: 'REL-KOSHER', label: 'Kosher', severity: 'strict' },
      { code: 'MED-CELIAC', label: 'Gluten-free (celiac)', severity: 'medical', glyph: 'wheat' },
      { code: 'INT-LACTOSE', label: 'Lactose-free', severity: 'medical', glyph: 'milk' },
      { code: 'LIFE-NOALCOHOL', label: 'No alcohol', severity: 'strict', glyph: 'wine' },
    ],
  },
];

/** Who sees the answers: said wherever they are asked or shown. */
export const ALLERGIES_DIET_AUDIENCE = 'Shared with the captain, chef, and chief stew.';

export const dietGroup = (id: DietGroupId) => DIET_GROUPS.find((g) => g.id === id)!;

/* ── The register ─────────────────────────────────────────────── */

export type DietRegisterEntry = {
  code: string;
  name: string;
  severity: DietSeverity;
  aliases: string;
};

export type DietRegister = Record<DietGroupId, DietRegisterEntry[]> & {
  byCode: Map<string, DietRegisterEntry>;
};

type RegisterRow = [string, string, DietSeverity, string?];

let register: Promise<DietRegister> | undefined;

/** The register, fetched once. Rejects on a failed fetch, and the next call tries again. */
export const loadDietRegister = (): Promise<DietRegister> => {
  register ??= fetch(registerUrl)
    .then((res) => res.json() as Promise<Record<DietGroupId, RegisterRow[]>>)
    .then((raw) => {
      const toEntry = ([code, name, severity, aliases = '']: RegisterRow): DietRegisterEntry => ({
        code,
        name,
        severity,
        aliases,
      });
      const allergy = raw.allergy.map(toEntry);
      const diet = raw.diet.map(toEntry);
      return {
        allergy,
        diet,
        byCode: new Map([...allergy, ...diet].map((entry) => [entry.code, entry])),
      };
    })
    .catch((error: unknown) => {
      register = undefined;
      throw error;
    });
  return register;
};

/** The register once it has loaded; undefined until then, or if it could not load. */
export const useDietRegister = (): DietRegister | undefined => {
  const [loaded, setLoaded] = useState<DietRegister>();
  useEffect(() => {
    let live = true;
    loadDietRegister()
      .then((value) => live && setLoaded(value))
      .catch((error: unknown) => console.error('Couldn’t load the dietary register:', error));
    return () => {
      live = false;
    };
  }, []);
  return loaded;
};

/** Up to six register entries whose name or aliases hold every word typed. */
export const searchDietRegister = (reg: DietRegister, group: DietGroupId, query: string) => {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const words = q.split(/\s+/);
  return reg[group]
    .filter((entry) => {
      const hay = `${entry.name} ${entry.aliases}`.toLowerCase();
      return words.every((word) => hay.includes(word));
    })
    .sort(
      (a, b) =>
        Number(!a.name.toLowerCase().startsWith(q)) - Number(!b.name.toLowerCase().startsWith(q)) ||
        a.name.length - b.name.length,
    )
    .slice(0, 6);
};

const quickPick = (code: string) =>
  DIET_GROUPS.flatMap((g) => g.quick).find((pick) => pick.code === code);

/** A pick's words: its short label when it is a common one, else its register name. */
export const dietLabel = (code: string, reg?: DietRegister) =>
  quickPick(code)?.label ?? reg?.byCode.get(code)?.name;

export const dietSeverity = (code: string, reg?: DietRegister) =>
  quickPick(code)?.severity ?? reg?.byCode.get(code)?.severity;

/* ── The answers ──────────────────────────────────────────────── */

export type DietAnswer = {
  /** Register codes, in the order they were picked. */
  codes: string[];
  /** What was added in the person's own words. */
  other: string[];
  note: string;
};

export type AllergiesDiet = Record<DietGroupId, DietAnswer>;

export const EMPTY_DIET_ANSWER: DietAnswer = { codes: [], other: [], note: '' };

export const EMPTY_ALLERGIES_DIET: AllergiesDiet = {
  allergy: EMPTY_DIET_ANSWER,
  diet: EMPTY_DIET_ANSWER,
};

/** How many things a group names: picks and free text, not the note. */
export const dietCount = (answer: DietAnswer) => answer.codes.length + answer.other.length;

/** A group's answer as words: the picks, then the free text. Skips a pick the register has not named yet. */
export const dietLabels = (answer: DietAnswer, reg?: DietRegister) => [
  ...answer.codes.flatMap((code) => dietLabel(code, reg) ?? []),
  ...answer.other,
];

/** `allergies` is capped at 2000 characters by the people service. */
export const ALLERGIES_MAX_LENGTH = 2000;

type StoredAnswer = { codes?: string[]; other?: string[]; note?: string };
type Stored = { v: 1; allergy?: StoredAnswer; diet?: StoredAnswer };

const toStored = ({ codes, other, note }: DietAnswer): StoredAnswer | undefined => {
  const stored: StoredAnswer = {
    ...(codes.length ? { codes } : {}),
    ...(other.length ? { other } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
  };
  return Object.keys(stored).length ? stored : undefined;
};

const fromStored = (stored?: StoredAnswer): DietAnswer => ({
  codes: stored?.codes ?? [],
  other: stored?.other ?? [],
  note: stored?.note ?? '',
});

/**
 * The two wire fields for these answers. `allergies` is empty when nothing is
 * answered, so an empty profile stays empty rather than holding `{"v":1}`.
 */
export const toAllergiesDietFields = (
  answers: AllergiesDiet,
  reg: DietRegister,
): Pick<PersonPreferences, 'allergies' | 'dietary_restrictions'> => {
  const allergy = toStored(answers.allergy);
  const diet = toStored(answers.diet);
  return {
    allergies: allergy || diet ? JSON.stringify({ v: 1, allergy, diet } satisfies Stored) : '',
    dietary_restrictions: dietLabels(answers.diet, reg),
  };
};

const parseStored = (allergies: string): Stored | undefined => {
  if (!allergies.trimStart().startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(allergies) as Stored;
    return parsed?.v === 1 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/* Before this, allergies were chips joined with ", " plus whatever was typed, and
   "No allergies" was a chip of its own. Entries are matched by name; without the
   register only the common picks can match, so the rest stay free text until it loads. */
const LEGACY_NONE = new Set(['no allergies']);

const fromLegacy = (entries: string[], group: DietGroupId, reg?: DietRegister): DietAnswer => {
  const byName = new Map<string, string>();
  reg?.[group].forEach((entry) => {
    if (!byName.has(entry.name.toLowerCase())) byName.set(entry.name.toLowerCase(), entry.code);
  });
  dietGroup(group).quick.forEach((pick) => byName.set(pick.label.toLowerCase(), pick.code));
  const answer: DietAnswer = { codes: [], other: [], note: '' };
  entries
    .map((entry) => entry.trim())
    .filter((entry) => entry && !LEGACY_NONE.has(entry.toLowerCase()))
    .forEach((entry) => {
      const code = byName.get(entry.toLowerCase());
      if (code && !answer.codes.includes(code)) answer.codes.push(code);
      else if (!code && !answer.other.includes(entry)) answer.other.push(entry);
    });
  return answer;
};

/** The two stored fields these answers are read from. */
type StoredFields = { allergies?: string | null; dietary_restrictions?: string[] | null };

/** A person's stored preferences as answers, from the current JSON or the old plain text. */
export const parseAllergiesDiet = (
  preferences: StoredFields | null | undefined,
  reg?: DietRegister,
): AllergiesDiet => {
  const allergies = preferences?.allergies ?? '';
  const stored = parseStored(allergies);
  if (stored) return { allergy: fromStored(stored.allergy), diet: fromStored(stored.diet) };
  return {
    allergy: fromLegacy(allergies.split(/[;,]/), 'allergy', reg),
    diet: fromLegacy(preferences?.dietary_restrictions ?? [], 'diet', reg),
  };
};

/** A person's answers, read again once the register loads so older plain text matches it. */
export const useAllergiesDiet = (preferences: StoredFields | null | undefined): AllergiesDiet => {
  const reg = useDietRegister();
  const allergies = preferences?.allergies;
  const dietary = preferences?.dietary_restrictions;
  return useMemo(
    () => parseAllergiesDiet({ allergies, dietary_restrictions: dietary }, reg),
    [allergies, dietary, reg],
  );
};
